import type { EdmDateTimeOffset, EdmGuid, TokenType } from "../odata-types.ts";
import { Expression } from "./base/Expression.ts";
import type { ComparisonValue, PropertyInfo } from "./base/types.ts";
import { stringifyLiteralValue } from "./LiteralExpression.ts";
import type { PropertyExpression } from "./PropertyExpression.ts";

export class ComparisonExpression extends Expression {
    readonly type = "comparison";
    public operator:
        | typeof TokenType.EQUALS
        | typeof TokenType.NOT_EQUALS
        | typeof TokenType.GREATER_THAN
        | typeof TokenType.GREATER_THAN_OR_EQUALS
        | typeof TokenType.LESS_THAN
        | typeof TokenType.LESS_THAN_OR_EQUALS;

    public left: PropertyExpression;
    public right: ComparisonValue;

    constructor(
        operator:
            | typeof TokenType.EQUALS
            | typeof TokenType.NOT_EQUALS
            | typeof TokenType.GREATER_THAN
            | typeof TokenType.GREATER_THAN_OR_EQUALS
            | typeof TokenType.LESS_THAN
            | typeof TokenType.LESS_THAN_OR_EQUALS,
        left: PropertyExpression,
        right: ComparisonValue
    ) {
        super();
        this.operator = operator;
        this.left = left;
        this.right = right;
    }

    stringify(): string {
        const left = this.left.stringify();
        const right = this.stringifyRight();

        return `${left} ${this.operator} ${right}`;
    }

    private stringifyRight(): string {
        switch (this.right.type) {
            case "literal":
                return stringifyLiteralValue(this.right.value);
            case "now":
                return "now()";
            case "relativeDate":
                return `now() ${this.right.operator} duration'${this.right.duration}'`;
        }
    }

    getKey(): string {
        const propertyName = this.left.toPathString();

        switch (this.right.type) {
            case "now":
                return `${this.operator}::${propertyName}::now`;
            case "relativeDate":
                return `${this.operator}::${propertyName}::${this.right.operator}::${this.right.duration}`;
            default:
                return `${this.operator}::${propertyName}::${this.right.value}`;
        }
    }

    getPropertyInfo(): PropertyInfo {
        return this.left.getPropertyInfo();
    }

    getLiteralValue(): string | number | boolean | null | EdmGuid | EdmDateTimeOffset | undefined {
        return this.right.type === "literal" ? this.right.value : undefined;
    }

    isNullComparison(): boolean {
        return this.right.type === "literal" && this.right.value === null;
    }

    isRelativeDate(): boolean {
        return this.right.type === "relativeDate";
    }

    getRelativeDateDuration(): string | undefined {
        return this.right.type === "relativeDate" ? this.right.duration : undefined;
    }
}
